import React from 'react';
import PanelLayout from '../../components/PanelLayout/PanelLayout';
import { usePageLayout } from '../../hooks/usePageLayout';
import { useEditMode } from '../../hooks/useEditMode';
import { EditLayoutProvider, useEditLayout, bottomPanelToInitialLayers } from '../../contexts/EditLayoutContext';
import { EditModeLookupsProvider } from '../../contexts/EditModeLookupsContext';
import type { PageType } from '../../hooks/usePageLayout';
import type { LayoutPanel } from '../../types/layout';
import './PageBase.css';

export interface PageBaseProps {
  pageType: PageType;
  resourceId: number | null;
  /** Left panel title */
  leftPanelTitle: string;
  /** Right panel title */
  rightPanelTitle: string;
  /** Top panel modules (or null). Can be string[] or Array<{id, name, ...}> for Stock. */
  topPanelModules?: string[] | Array<{ id: string; name: string; resource_id?: number; page_order?: number }> | null;
  /** Whether bottom panel is visible */
  bottomPanelVisible?: boolean;
  /** Optional extra props for PanelLayout (e.g. onSpeciesLEKLoad for Stock) */
  panelLayoutProps?: Record<string, unknown>;
}

interface PageBaseContentProps extends PageBaseProps {
  layoutLeftPanel: LayoutPanel | null;
  layoutRightPanel: LayoutPanel | null;
  layoutBottomLayers: Record<string, Record<string, unknown> | null> | null;
}

/**
 * Inner content that uses edit layout when in edit mode.
 */
function PageBaseContent({
  pageType,
  resourceId,
  leftPanelTitle,
  rightPanelTitle,
  topPanelModules,
  bottomPanelVisible,
  panelLayoutProps,
  layoutLeftPanel,
  layoutRightPanel,
  layoutBottomLayers,
}: PageBaseContentProps): React.ReactElement {
  const isEditMode = useEditMode();
  const editLayout = useEditLayout();

  const effectiveLeftPanel =
    isEditMode && editLayout ? editLayout.editableLayout.leftPanel : layoutLeftPanel;
  const effectiveRightPanel =
    isEditMode && editLayout ? editLayout.editableLayout.rightPanel : layoutRightPanel;
  const effectiveBottomLayers =
    isEditMode && editLayout
      ? bottomPanelToInitialLayers(editLayout.editableLayout.bottomPanel)
      : layoutBottomLayers ?? undefined;

  const { pageType: _omitPageType, resourceId: _omitResourceId, ...restPanelLayoutProps } = panelLayoutProps ?? {};
  const cameraProps = { pageType, resourceId } as any;
  return (
    <PanelLayout
      leftPanelTitle={leftPanelTitle}
      leftPanelLayout={effectiveLeftPanel ?? undefined}
      rightPanelTitle={rightPanelTitle}
      rightPanelLayout={effectiveRightPanel ?? undefined}
      leftPanelInjections={undefined}
      rightPanelInjections={undefined}
      topPanelModules={topPanelModules}
      bottomPanelInitialLayers={effectiveBottomLayers ?? layoutBottomLayers ?? undefined}
      bottomPanelVisible={bottomPanelVisible}
      editMode={isEditMode}
      onSpeciesLEKLoad={undefined}
      onToggleSpeciesLEKData={undefined}
      onModuleSelect={undefined}
      selectedModuleId={undefined}
      {...restPanelLayoutProps}
      {...cameraProps}
    />
  );
}

/**
 * Page wrapper that fetches layout from the backend, applies camera, and renders PanelLayout.
 * Used by Home, Region, Plan, Stock.
 * When ?edit=true, wraps with EditLayoutProvider for layout editing.
 */
export default function PageBase({
  pageType,
  resourceId,
  leftPanelTitle,
  rightPanelTitle,
  topPanelModules = null,
  bottomPanelVisible = true,
  panelLayoutProps = {},
}: PageBaseProps): React.ReactElement {
  const {
    loading: layoutLoading,
    error: layoutError,
    bottomPanelInitialLayers: layoutBottomLayers,
    leftPanel: layoutLeftPanel,
    rightPanel: layoutRightPanel,
    bottomPanel: layoutBottomPanel,
  } = usePageLayout({ pageType, resourceId });

  const isEditMode = useEditMode();

  if (layoutLoading) {
    return <div className="loading-state">Loading page data...</div>;
  }

  if (layoutError) {
    return <div className="loading-state">Unable to load page data.</div>;
  }

  const content = (
    <PageBaseContent
      pageType={pageType}
      resourceId={resourceId}
      leftPanelTitle={leftPanelTitle}
      rightPanelTitle={rightPanelTitle}
      topPanelModules={topPanelModules}
      bottomPanelVisible={bottomPanelVisible}
      panelLayoutProps={panelLayoutProps}
      layoutLeftPanel={layoutLeftPanel}
      layoutRightPanel={layoutRightPanel}
      layoutBottomLayers={layoutBottomLayers}
    />
  );

  if (isEditMode) {
    return (
      <EditLayoutProvider
        isEditMode={true}
        apiLeftPanel={layoutLeftPanel}
        apiRightPanel={layoutRightPanel}
        apiBottomPanel={layoutBottomPanel}
      >
        <EditModeLookupsProvider>
          {content}
        </EditModeLookupsProvider>
      </EditLayoutProvider>
    );
  }

  return content;
}
